import { createContext, useContext, useEffect, useState } from "react";
import api, { describeApiError, tokenStore } from "./api";

const AuthContext = createContext(null);

// Shape the auth context user object the same way every entry point does, so
// downstream code can rely on `is_admin` and `can_edit_curriculum` flags.
function shapeUser(data) {
  return {
    id: data.id,
    email: data.email,
    name: data.name,
    role: data.role,
    is_admin: !!data.is_admin,
    can_edit_curriculum: !!data.can_edit_curriculum,
  };
}

export function AuthProvider({ children }) {
  // null = checking (probing /auth/me), false = unauthenticated, object = signed in.
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    // If there's no Bearer token in sessionStorage we can short-circuit and
    // skip the /auth/me probe entirely — saves a flash of loading state and
    // an unnecessary 401 on first paint.
    if (!tokenStore.get()) {
      setUser(false);
      return () => {};
    }
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (!cancelled) setUser(data);
      })
      .catch(() => {
        if (!cancelled) {
          tokenStore.clear();
          setUser(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (identifier, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { identifier, password });
      // Sanity-check: the backend MUST return a token for Bearer auth to work
      // on mobile. If it doesn't, surface that loudly instead of failing
      // silently the next time the user tries to access a protected page.
      if (!data?.token) {
        console.error("Login response missing token field:", data);
        setError("Login succeeded but no auth token was returned. Please contact support.");
        return false;
      }
      tokenStore.set(data.token);
      setUser(shapeUser(data));
      return true;
    } catch (e) {
      console.error("Login failed:", e);
      setError(describeApiError(e));
      return false;
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      if (!data?.token) {
        console.error("Register response missing token field:", data);
        setError("Sign-up succeeded but no auth token was returned. Please contact support.");
        return false;
      }
      tokenStore.set(data.token);
      setUser(shapeUser(data));
      return true;
    } catch (e) {
      console.error("Register failed:", e);
      setError(describeApiError(e));
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed, clearing local session anyway", e);
    }
    tokenStore.clear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, error, setError }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
